import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import I18n from '../../config/i18n';
import { RootState } from '../../config/redux/types';
import { Accommodation } from '../../routes/reducers/types';

interface AccommodationLinkProps {
  className?: string;
  children: React.ReactNode;
  accommodation: Pick<Accommodation, 'slug'>;
  target?: '_self' | '_blank';
}

export function accommodationLinkPath(
  lang: string | undefined,
  accommodation: Pick<Accommodation, 'slug'>,
  arrival: moment.Moment | null,
  durationOfStay: number | null,
  occupancy: number | null,
): string {
  const path = `/${lang || I18n.defaultLocale}/${encodeURIComponent(accommodation.slug)}`;
  const searchParams = new URLSearchParams();
  if (arrival) {
    searchParams.set('arrival', arrival.format('YYYY-MM-DD'));
  }
  if (durationOfStay != null) {
    searchParams.set('duration_of_stay', durationOfStay.toString());
  }
  if (occupancy != null) {
    searchParams.set('max_occupancy', occupancy.toString());
  }
  const search = searchParams.toString();

  if (search) {
    return [path, search].join('?');
  }
  return path;
}

export default function AccommodationLink({
  accommodation,
  className,
  children,
  target,
}: AccommodationLinkProps): JSX.Element {
  const { lang } = useParams<'lang'>();
  const { arrival, durationOfStay, occupancy } = useSelector((state: RootState) => state.request);

  return (
    <Link
      className={className}
      to={accommodationLinkPath(lang, accommodation, arrival, durationOfStay, occupancy)}
      target={target}
    >
      {children}
    </Link>
  );
}

const defaultProps: Pick<AccommodationLinkProps, 'className' | 'target'> = {
  className: undefined,
  target: undefined,
};

AccommodationLink.defaultProps = defaultProps;
